import { useState, useCallback, useEffect } from 'react';
import { 
  deriveKey, 
  encrypt as cryptoEncrypt, 
  decrypt as cryptoDecrypt, 
  generateSalt,
  APP_ENCRYPTION_SALT,
  type EncryptedBlob 
} from '@/lib/crypto';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/integrations/supabase/client';
import { 
  setE2ECryptoKey,
  getMasterKeyForUser,
  putMasterKeyForUser,
  clearMasterKeyForUser,
} from '@/lib/cryptoKeyStore';
import { getE2ESetupKey } from '@/utils/storageKeys';

// In-memory key cache (never persisted in plain global state, scoped to session)
let cachedCryptoKey: CryptoKey | null = null;
let cachedUserId: string | null = null;

// Synchronous Base64 conversion helpers for Web Crypto raw keys
function arrayBufferToBase64(buffer: ArrayBuffer): string {
  const bytes = new Uint8Array(buffer);
  let binary = "";
  for (let i = 0; i < bytes.byteLength; i++) {
    binary += String.fromCharCode(bytes[i]);
  }
  return btoa(binary);
}

function base64ToArrayBuffer(base64: string): ArrayBuffer {
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i);
  }
  return bytes.buffer;
}

// Generate random recovery key string
function generateRecoveryKey(): string {
  const chars = "ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789";
  let result = "DECKLE-RESTORE";
  const randomValues = new Uint8Array(12);
  crypto.getRandomValues(randomValues);
  for (let i = 0; i < 12; i++) {
    if (i % 4 === 0) result += "-";
    result += chars[randomValues[i] % chars.length];
  }
  return result;
}

export function useEncryption() {
  const { user } = useAuth();

  // Synchronously invalidate in-memory key cache if user changed
  if (cachedUserId && cachedUserId !== user?.id) {
    cachedCryptoKey = null;
    cachedUserId = null;
    setE2ECryptoKey(null, null);
  }

  const [isReady, setIsReady] = useState(
    cachedCryptoKey !== null && cachedUserId === user?.id
  );
  const [isSetup, setIsSetup] = useState<boolean | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Auto-load key and check setup status on mount / user change
  useEffect(() => {
    const userId = user?.id;
    if (!userId) {
      cachedCryptoKey = null;
      cachedUserId = null;
      setE2ECryptoKey(null, null);
      setIsReady(false);
      setIsSetup(null);
      setIsLoading(false);
      return;
    }

    if (cachedCryptoKey && cachedUserId === userId) {
      setIsReady(true);
      setIsSetup(true);
      setIsLoading(false);
      return;
    }

    // Reset state for new user
    setIsReady(false);
    setIsSetup(null);
    setError(null);
    let active = true;
    setIsLoading(true);

    async function checkSetupAndLoadKey() {
      try {
        // 1. Try to load master key from platform storage
        const localKeyBase64 = await getMasterKeyForUser(userId);
        if (localKeyBase64) {
          const masterKeyBuffer = base64ToArrayBuffer(localKeyBase64);
          const masterKey = await crypto.subtle.importKey(
            "raw",
            masterKeyBuffer,
            { name: "AES-GCM" },
            false,
            ["encrypt", "decrypt"]
          );
          if (active) {
            cachedCryptoKey = masterKey;
            cachedUserId = userId;
            setE2ECryptoKey(masterKey, userId);
            setIsReady(true);
            setIsSetup(true);
            setIsLoading(false);
          }
          return;
        }

        // 2. Check if E2E is configured in the cloud or via local flag
        const isLocalSetup = localStorage.getItem(getE2ESetupKey(userId)) === "true";
        if (isLocalSetup) {
          if (active) {
            setIsSetup(true);
            setIsLoading(false);
          }
          return;
        }

        // Fetch sentinel row from Supabase
        const { data, error: queryError } = await supabase
          .from("manuscript_backups")
          .select("id")
          .eq("manuscript_id", "__verification__")
          .eq("user_id", userId)
          .maybeSingle();

        if (queryError) {
          throw queryError;
        }

        if (active) {
          if (data) {
            setIsSetup(true);
            localStorage.setItem(getE2ESetupKey(userId), "true");
          } else {
            setIsSetup(false);
          }
        }
      } catch (err) {
        console.error("[Encryption] Failed to check setup or load key:", err);
        if (active) {
          setError(err instanceof Error ? err.message : "Failed to initialize encryption");
        }
      } finally {
        if (active) {
          setIsLoading(false);
        }
      }
    }

    checkSetupAndLoadKey();

    return () => {
      active = false;
    };
  }, [user?.id]);

  // Setup E2E Encryption
  const setupEncryption = useCallback(async (passphrase: string): Promise<{ recoveryKey: string }> => {
    if (!user?.id) throw new Error("Not authenticated");

    setIsLoading(true);
    setError(null);

    try {
      // Check if sentinel already exists to prevent destructive overwrites
      const { data: existingSentinel } = await supabase
        .from("manuscript_backups")
        .select("id")
        .eq("user_id", user.id)
        .eq("manuscript_id", "__verification__")
        .maybeSingle();

      if (existingSentinel) {
        throw new Error("Encryption is already configured for this account. Please unlock with your existing passphrase or recovery key.");
      }

      // 1. Generate random 256-bit Master Key
      const masterKeyBytes = crypto.getRandomValues(new Uint8Array(32));
      const masterKeyBase64 = arrayBufferToBase64(masterKeyBytes.buffer);

      // 2. Generate random salts
      const passphraseSaltBytes = generateSalt();
      const recoverySaltBytes = generateSalt();

      // 3. Generate Recovery Key
      const recoveryKey = generateRecoveryKey();

      // 4. Derive keys
      const keyPassphrase = await deriveKey(passphrase, passphraseSaltBytes);
      const keyRecovery = await deriveKey(recoveryKey, recoverySaltBytes);

      // 5. Encrypt Master Key
      const encryptedMasterKeyPassphrase = await cryptoEncrypt(masterKeyBase64, keyPassphrase, passphraseSaltBytes);
      const encryptedMasterKeyRecovery = await cryptoEncrypt(masterKeyBase64, keyRecovery, recoverySaltBytes);

      // 6. Construct sentinel payload
      const verificationPayload = {
        version: 1,
        passphraseSalt: arrayBufferToBase64(passphraseSaltBytes.buffer),
        recoverySalt: arrayBufferToBase64(recoverySaltBytes.buffer),
        encryptedMasterKeyPassphrase,
        encryptedMasterKeyRecovery,
      };

      // 7. Store verification sentinel in Supabase (insert-only)
      const { error: insertError } = await supabase
        .from("manuscript_backups")
        .insert({
          user_id: user.id,
          manuscript_id: "__verification__",
          content: JSON.stringify(verificationPayload),
          is_encrypted: false,
          updated_at: new Date().toISOString(),
        });

      if (insertError) throw insertError;

      // 8. Import Master Key into Web Crypto
      const masterKey = await crypto.subtle.importKey(
        "raw",
        masterKeyBytes.buffer,
        { name: "AES-GCM" },
        false,
        ["encrypt", "decrypt"]
      );

      // 9. Cache key in memory and save to platform storage
      cachedCryptoKey = masterKey;
      cachedUserId = user.id;
      setE2ECryptoKey(masterKey, user.id);
      await putMasterKeyForUser(user.id, masterKeyBase64);
      localStorage.setItem(getE2ESetupKey(user.id), "true");

      setIsReady(true);
      setIsSetup(true);

      return { recoveryKey };
    } catch (err) {
      console.error("[Encryption] Setup failed:", err);
      const msg = err instanceof Error ? err.message : "Failed to setup encryption";
      setError(msg);
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, [user?.id]);

  // Unlock with Passphrase
  const unlockWithPassphrase = useCallback(async (passphrase: string): Promise<boolean> => {
    if (!user?.id) throw new Error("Not authenticated");

    setIsLoading(true);
    setError(null);

    try {
      // 1. Fetch verification row
      const { data, error: fetchError } = await supabase
        .from("manuscript_backups")
        .select("content")
        .eq("manuscript_id", "__verification__")
        .eq("user_id", user.id)
        .maybeSingle();

      if (fetchError || !data) {
        throw new Error(fetchError?.message || "Encryption setup not found in the cloud.");
      }

      const payload = JSON.parse(data.content);
      const passphraseSaltBytes = new Uint8Array(base64ToArrayBuffer(payload.passphraseSalt));

      // 2. Derive key from passphrase
      const keyPassphrase = await deriveKey(passphrase, passphraseSaltBytes);

      // 3. Decrypt Master Key
      const masterKeyBase64 = await cryptoDecrypt(payload.encryptedMasterKeyPassphrase, keyPassphrase);

      // 4. Import Master Key
      const masterKeyBuffer = base64ToArrayBuffer(masterKeyBase64);
      const masterKey = await crypto.subtle.importKey(
        "raw",
        masterKeyBuffer,
        { name: "AES-GCM" },
        false,
        ["encrypt", "decrypt"]
      );

      // 5. Cache in memory and save locally
      cachedCryptoKey = masterKey;
      cachedUserId = user.id;
      setE2ECryptoKey(masterKey, user.id);
      await putMasterKeyForUser(user.id, masterKeyBase64);
      localStorage.setItem(getE2ESetupKey(user.id), "true");

      setIsReady(true);
      return true;
    } catch (err) {
      console.error("[Encryption] Passphrase unlock failed:", err);
      setError("Incorrect passphrase.");
      return false;
    } finally {
      setIsLoading(false);
    }
  }, [user?.id]);

  // Unlock with Recovery Key
  const unlockWithRecoveryKey = useCallback(async (recoveryKey: string): Promise<boolean> => {
    if (!user?.id) throw new Error("Not authenticated");

    setIsLoading(true);
    setError(null);

    try {
      // 1. Fetch verification row
      const { data, error: fetchError } = await supabase
        .from("manuscript_backups")
        .select("content")
        .eq("manuscript_id", "__verification__")
        .eq("user_id", user.id)
        .maybeSingle();

      if (fetchError || !data) {
        throw new Error(fetchError?.message || "Encryption setup not found in the cloud.");
      }

      const payload = JSON.parse(data.content);
      const recoverySaltBytes = new Uint8Array(base64ToArrayBuffer(payload.recoverySalt));

      // 2. Derive key from recovery key
      const keyRecovery = await deriveKey(recoveryKey.trim().toUpperCase(), recoverySaltBytes);

      // 3. Decrypt Master Key
      const masterKeyBase64 = await cryptoDecrypt(payload.encryptedMasterKeyRecovery, keyRecovery);

      // 4. Import Master Key
      const masterKeyBuffer = base64ToArrayBuffer(masterKeyBase64);
      const masterKey = await crypto.subtle.importKey(
        "raw",
        masterKeyBuffer,
        { name: "AES-GCM" },
        false,
        ["encrypt", "decrypt"]
      );

      // 5. Cache in memory and save locally
      cachedCryptoKey = masterKey;
      cachedUserId = user.id;
      setE2ECryptoKey(masterKey, user.id);
      await putMasterKeyForUser(user.id, masterKeyBase64);
      localStorage.setItem(getE2ESetupKey(user.id), "true");

      setIsReady(true);
      return true;
    } catch (err) {
      console.error("[Encryption] Recovery key unlock failed:", err);
      setError("Invalid recovery key.");
      return false;
    } finally {
      setIsLoading(false);
    }
  }, [user?.id]);

  // Clear local keys (on logout)
  const clearEncryption = useCallback(async () => {
    const uid = cachedUserId ?? user?.id;
    cachedCryptoKey = null;
    cachedUserId = null;
    setE2ECryptoKey(null, null);
    setIsReady(false);
    setIsSetup(null);
    await clearMasterKeyForUser(uid);
  }, [user?.id]);

  // Encrypt content for cloud storage using E2E Master Key
  const encryptContent = useCallback(async (plaintext: string): Promise<string | null> => {
    if (!cachedCryptoKey || cachedUserId !== user?.id) return null;

    const encrypted = await cryptoEncrypt(plaintext, cachedCryptoKey, generateSalt());
    return JSON.stringify(encrypted);
  }, [user?.id]);

  // Decrypt content from cloud storage
  const decryptContent = useCallback(async (encryptedJson: string): Promise<string | null> => {
    let key = cachedCryptoKey;

    // Fallback: if E2E is not setup yet, try deriving key from user.id to decrypt legacy backups
    if (!key && user?.id) {
      try {
        key = await deriveKey(user.id, APP_ENCRYPTION_SALT);
      } catch (err) {
        console.error('[Encryption] Failed to derive legacy key for decryption:', err);
      }
    }

    if (!key) return null;

    try {
      const parsed = JSON.parse(encryptedJson);
      // If content is already a plaintext payload object (e.g. contains manuscript/plannerStructure)
      if (parsed && typeof parsed === 'object' && !parsed.ciphertext && (parsed.manuscript || parsed.plannerStructure || parsed.scenes || parsed.id)) {
        return encryptedJson;
      }

      const encryptedBlob: EncryptedBlob = parsed;
      try {
        return await cryptoDecrypt(encryptedBlob, key);
      } catch (decryptError) {
        // If decryption with the main key (E2E Master Key) failed, try the legacy key fallback
        if (key === cachedCryptoKey && user?.id) {
          console.log('[Encryption] Decryption with master key failed, attempting legacy key fallback...');
          try {
            const legacyKey = await deriveKey(user.id, APP_ENCRYPTION_SALT);
            return await cryptoDecrypt(encryptedBlob, legacyKey);
          } catch (legacyError) {
            console.error('[Encryption] Legacy fallback decryption also failed:', legacyError);
            throw decryptError;
          }
        }
        throw decryptError;
      }
    } catch (error) {
      console.error('[Encryption] Failed to decrypt content:', error);
      return null;
    }
  }, [user?.id]);

  return {
    isReady,
    isSetup,
    isLoading,
    error,
    setupEncryption,
    unlockWithPassphrase,
    unlockWithRecoveryKey,
    clearEncryption,
    encryptContent,
    decryptContent,
  };
}
